/**
 * IVM Animation Presets — 18 presets + 2 filters
 *
 * Categories: Diffusion (3), Life (3), Oscillators (3),
 *             Dynamics (4), Patterns (4), Meta (1), Filters (2)
 *
 * Engine copies current→next before update(), so presets only
 * write the channels they modify. No stale-buffer issues.
 */

import type { IVMPreset, IVMFilter, IVMContext, VisualOutput } from './ivm-engine';
import { LUT, CLAN_LUTS, RIVER_LUTS, lutLookup } from './ivm-colors';

// === Helpers ===

/** Get the 3 neighbor indices for triangle i. Returns -1 for boundary. */
function nb(ctx: IVMContext, i: number): [number, number, number] {
  const base = i * 3;
  return [ctx.neighbors[base], ctx.neighbors[base + 1], ctx.neighbors[base + 2]];
}

/** Average a channel across valid neighbors of i */
function avgNeighbor(arr: Float32Array, ctx: IVMContext, i: number): number {
  const [n0, n1, n2] = nb(ctx, i);
  let sum = 0;
  let count = 0;
  if (n0 >= 0) { sum += arr[n0]; count++; }
  if (n1 >= 0) { sum += arr[n1]; count++; }
  if (n2 >= 0) { sum += arr[n2]; count++; }
  return count > 0 ? sum / count : arr[i];
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

// ============================================================
// DIFFUSION (2)
// ============================================================

/** 1. cathedral_whisper — Energy diffuses with orientation bias + exhale pulse */
const cathedral_whisper: IVMPreset = {
  id: 'cathedral_whisper',
  name: 'Cathedral Whisper',
  category: 'Diffusion',
  init(ctx) {
    for (let i = 0; i < ctx.count; i++) {
      ctx.current.energy[i] = Math.random() * 0.3;
    }
  },
  update(ctx) {
    const { energy } = ctx.current;
    const next = ctx.next.energy;
    // next already seeded from current by engine
    for (let i = 0; i < ctx.count; i++) {
      const avg = avgNeighbor(energy, ctx, i);
      const bias = ctx.orientations[i] === 1 ? 0.002 : -0.001;
      let e = energy[i] + (avg - energy[i]) * 0.08 + bias;
      // Exhale pulse: drop self, push into neighbors
      if (e > 0.9) {
        e = 0;
        const [n0, n1, n2] = nb(ctx, i);
        // Accumulate on top of seeded next values (safe — no stale data)
        if (n0 >= 0) next[n0] = Math.min(1, next[n0] + 0.15);
        if (n1 >= 0) next[n1] = Math.min(1, next[n1] + 0.15);
        if (n2 >= 0) next[n2] = Math.min(1, next[n2] + 0.15);
      }
      next[i] = clamp01(e);
    }
  },
  visual(i, ctx) {
    const e = ctx.current.energy[i];
    return {
      opacity: 0.15 + e * 0.85,
      scale: 0.85 + e * 0.15,
      rotation: 0,
      color: lutLookup(LUT.cathedral, e),
    };
  },
};

/** 2. thermal_gradient — Heat diffusion top→bottom with random injections */
const thermal_gradient: IVMPreset = {
  id: 'thermal_gradient',
  name: 'Thermal Gradient',
  category: 'Diffusion',
  init(ctx) {
    for (let i = 0; i < ctx.count; i++) {
      ctx.current.temperature[i] = 1 - ctx.normalizedY[i];
    }
  },
  update(ctx) {
    const { temperature } = ctx.current;
    const next = ctx.next.temperature;
    for (let i = 0; i < ctx.count; i++) {
      const avg = avgNeighbor(temperature, ctx, i);
      let t = temperature[i] + (avg - temperature[i]) * 0.05;
      // Boundary bias: top hot, bottom cold
      t += (1 - ctx.normalizedY[i] - t) * 0.002;
      next[i] = clamp01(t);
    }
    // Random heat injection every 60 frames
    if (ctx.frame % 60 === 0 && ctx.count > 0) {
      const idx = Math.floor(Math.random() * ctx.count);
      next[idx] = 1;
    }
  },
  visual(i, ctx) {
    const t = ctx.current.temperature[i];
    return {
      opacity: 0.2 + t * 0.8,
      scale: 0.9 + t * 0.1,
      rotation: 0,
      color: lutLookup(LUT.thermal, t),
    };
  },
};

// ============================================================
// LIFE (2)
// ============================================================

/** 4. conway_trinity — Organic growth from 3 seeds
 *
 *  Continuous diffusion with bistable dynamics (not a discrete CA).
 *  Energy spreads from seeds to neighbors. Cells above a threshold
 *  are self-sustaining; below it they fade. Dense regions get gentle
 *  overcrowding pressure → perpetual breathing waves. Mouse paints energy.
 */
const conway_trinity: IVMPreset = {
  id: 'conway_trinity',
  name: 'Conway Trinity',
  category: 'Life',
  init(ctx) {
    const seeds = [
      { x: 0.3, y: 0.35 },
      { x: 0.7, y: 0.35 },
      { x: 0.5, y: 0.7 },
    ];
    for (const s of seeds) {
      let bestIdx = 0, minDist = Infinity;
      for (let i = 0; i < ctx.count; i++) {
        const dx = ctx.normalizedX[i] - s.x;
        const dy = ctx.normalizedY[i] - s.y;
        const d = dx * dx + dy * dy;
        if (d < minDist) { minDist = d; bestIdx = i; }
      }
      ctx.current.energy[bestIdx] = 1;
    }
  },
  update(ctx) {
    const { energy } = ctx.current;
    const next = ctx.next.energy;
    for (let i = 0; i < ctx.count; i++) {
      const avg = avgNeighbor(energy, ctx, i);
      let e = energy[i];
      e += (avg - e) * 0.015;                      // diffuse
      e -= 0.0003;                                  // gentle decay
      if (e > 0.1) e += 0.002;                     // self-sustaining growth
      if (e > 0.6 && avg > 0.55) e -= 0.003;       // overcrowding pressure
      if (ctx.mouseActive) {
        const dx = ctx.normalizedX[i] - ctx.mouseX;
        const dy = ctx.normalizedY[i] - ctx.mouseY;
        const d2 = dx * dx + dy * dy;
        if (d2 < 0.012) e += 0.006 * (1 - d2 / 0.012);
      }
      next[i] = clamp01(e);
    }
  },
  visual(i, ctx) {
    const e = ctx.current.energy[i];
    return {
      opacity: 0.15 + e * 0.45,
      scale: 0.92 + e * 0.08,
      rotation: 0,
      color: lutLookup(LUT.conway, e),
    };
  },
};

/** 6. wound_healing — Cluster wounds heal edge-inward; mouse inflicts damage
 *
 *  Tissue starts healthy (energy ≈ 0.8). Init creates 3–5 wound clusters
 *  via BFS flood-fill. Only cells at the wound edge (touching healthy tissue)
 *  can heal, and healing is slow — so you see the wound visibly close inward.
 *  Inflammation (temperature) radiates outward from wound edges.
 *  Mouse click/drag creates new wound clusters. New random wounds periodically.
 */
const wound_healing: IVMPreset = {
  id: 'wound_healing',
  name: 'Wound Healing',
  category: 'Life',
  extraChannels: ['damaged', 'scar'],
  init(ctx) {
    const damaged = ctx.extra.get('damaged')!;
    const scar = ctx.extra.get('scar')!;
    for (let i = 0; i < ctx.count; i++) {
      ctx.current.energy[i] = 0.8;
      ctx.current.temperature[i] = 0;
      damaged[i] = 0;
      scar[i] = 0;
    }
    // Create 3–5 wound clusters via BFS (write to current during init)
    const numWounds = 3 + Math.floor(Math.random() * 3);
    for (let w = 0; w < numWounds; w++) {
      inflictWound(ctx, ctx.current.energy, ctx.current.temperature,
        Math.floor(Math.random() * ctx.count),
        Math.max(8, Math.floor(ctx.count * 0.015)));
    }
  },
  update(ctx) {
    const { energy, temperature } = ctx.current;
    const nextE = ctx.next.energy;
    const nextT = ctx.next.temperature;
    const damaged = ctx.extra.get('damaged')!;
    const scar = ctx.extra.get('scar')!;

    for (let i = 0; i < ctx.count; i++) {
      const isDmg = damaged[i] > 0.5;
      const [n0, n1, n2] = nb(ctx, i);

      if (isDmg) {
        // Edge detection: is this damaged cell adjacent to healthy tissue?
        let touchesHealthy = false;
        for (const ni of [n0, n1, n2]) {
          if (ni >= 0 && damaged[ni] < 0.5) { touchesHealthy = true; break; }
        }

        if (touchesHealthy) {
          // Edge cells heal slowly via neighbor donation
          for (const ni of [n0, n1, n2]) {
            if (ni >= 0 && damaged[ni] < 0.5 && energy[ni] > 0.3) {
              const donation = energy[ni] * 0.008;
              nextE[i] += donation;
              nextE[ni] -= donation * 0.3;
            }
          }
          // Once energy restored, mark as healed with scar
          if (nextE[i] > 0.6) {
            damaged[i] = 0;
            scar[i] = 1;
            nextT[i] = 0.3; // brief inflammation flash on heal
          }
        }
        // Interior wound cells: no healing, slight random flicker
        nextE[i] = clamp01(nextE[i] + (Math.random() - 0.5) * 0.01);
      } else {
        // Healthy tissue: slow regen toward baseline
        nextE[i] += (0.8 - energy[i]) * 0.005;
      }

      // Inflammation spreads outward from wound edges
      if (isDmg) {
        for (const ni of [n0, n1, n2]) {
          if (ni >= 0 && damaged[ni] < 0.5) {
            nextT[ni] = Math.min(1, nextT[ni] + 0.003);
          }
        }
      }
      nextT[i] *= 0.992;

      // Scar fades slowly
      if (scar[i] > 0) scar[i] = Math.max(0, scar[i] - 0.002);

      nextE[i] = clamp01(nextE[i]);
      nextT[i] = clamp01(nextT[i]);
    }

    // Periodic new wound cluster (write to next during update)
    if (ctx.frame % 300 === 0) {
      inflictWound(ctx, nextE, nextT,
        Math.floor(Math.random() * ctx.count),
        Math.max(5, Math.floor(ctx.count * 0.01)));
    }

    // Mouse creates wounds
    if (ctx.mouseActive) {
      for (let i = 0; i < ctx.count; i++) {
        const dx = ctx.normalizedX[i] - ctx.mouseX;
        const dy = ctx.normalizedY[i] - ctx.mouseY;
        if (dx * dx + dy * dy < 0.004) {
          nextE[i] = 0;
          nextT[i] = 1;
          damaged[i] = 1;
        }
      }
    }
  },
  visual(i, ctx) {
    const e = ctx.current.energy[i];
    const t = ctx.current.temperature[i];
    const isDmg = ctx.extra.get('damaged')![i] > 0.5;
    const sc = ctx.extra.get('scar')![i];
    // Wound interior: deep red. Healing edge: warm. Healthy: green. Scar: faint gold.
    const value = isDmg ? clamp01((1 - e) * 0.35)
      : sc > 0.01 ? clamp01(0.45 + sc * 0.15)
      : clamp01(0.5 + e * 0.5);
    return {
      opacity: isDmg ? 0.5 + t * 0.5 : 0.25 + e * 0.5 + t * 0.25,
      scale: isDmg ? 0.8 + t * 0.1 : 0.88 + e * 0.12,
      rotation: isDmg ? t * 4 : sc > 0.01 ? sc * 2 : 0,
      color: lutLookup(LUT.wound, value),
    };
  },
};

/** BFS flood-fill to create a contiguous wound cluster.
 *  Writes to the provided energy/temperature arrays (current for init, next for update). */
function inflictWound(
  ctx: IVMContext,
  eArr: Float32Array,
  tArr: Float32Array,
  seed: number,
  size: number,
): void {
  const damaged = ctx.extra.get('damaged')!;
  const queue = [seed];
  let head = 0;
  let filled = 0;
  eArr[seed] = 0;
  tArr[seed] = 1;
  damaged[seed] = 1;
  filled++;
  while (head < queue.length && filled < size) {
    const curr = queue[head++];
    const [n0, n1, n2] = nb(ctx, curr);
    for (const ni of [n0, n1, n2]) {
      if (ni >= 0 && damaged[ni] < 0.5 && filled < size) {
        eArr[ni] = 0;
        tArr[ni] = 1;
        damaged[ni] = 1;
        queue.push(ni);
        filled++;
      }
    }
  }
}

// ============================================================
// OSCILLATORS (3)
// ============================================================

/** 8. breathing_lattice — Global sine + radial phase offset, no neighbors */
const breathing_lattice: IVMPreset = {
  id: 'breathing_lattice',
  name: 'Breathing Lattice',
  category: 'Oscillators',
  init(ctx) {
    for (let i = 0; i < ctx.count; i++) {
      const dx = ctx.normalizedX[i] - 0.5;
      const dy = ctx.normalizedY[i] - 0.5;
      ctx.current.phase[i] = Math.sqrt(dx * dx + dy * dy) * 8;
    }
  },
  update(ctx) {
    const { phase } = ctx.current;
    const nextP = ctx.next.phase;
    const nextE = ctx.next.energy;
    for (let i = 0; i < ctx.count; i++) {
      nextP[i] = phase[i] + 0.02;
      nextE[i] = (Math.sin(nextP[i] + ctx.elapsed * 0.5) + 1) / 2;
    }
  },
  visual(i, ctx) {
    const e = ctx.current.energy[i];
    return {
      opacity: 0.1 + e * 0.7,
      scale: 0.9 + e * 0.1,
      rotation: 0,
      color: lutLookup(LUT.mono, 1 - e),
    };
  },
};

/** 9. zero_point_field — Quasicrystal from incommensurate frequencies */
const zero_point_field: IVMPreset = {
  id: 'zero_point_field',
  name: 'Zero Point Field',
  category: 'Oscillators',
  init() { /* purely computed from coordinates */ },
  update(ctx) {
    const nextE = ctx.next.energy;
    const w1 = 1.0, w2 = Math.PI / 2.3, w3 = Math.E / 2.1, w4 = 0.4;
    for (let i = 0; i < ctx.count; i++) {
      const val = Math.sin(ctx.coordA[i] * w1 + ctx.coordB[i] * w2 + ctx.coordC[i] * w3 + ctx.elapsed * w4);
      nextE[i] = (val + 1) / 2;
    }
  },
  visual(i, ctx) {
    const e = ctx.current.energy[i];
    return {
      opacity: 0.05 + e * 0.9,
      scale: 0.9 + e * 0.1,
      rotation: 0,
      color: lutLookup(LUT.cathedral, e),
    };
  },
};

// ============================================================
// PATTERNS (2)
// ============================================================

/** 14. three_rivers — Drops converge on center, ripple on impact
 *
 *  Drops spawn from the top-right edge and drift slowly toward the center,
 *  leaving a fading cyan trail. On impact they trigger a gold ripple that
 *  expands outward via the wave equation on the triangle grid.
 *  Mouse click creates additional ripples.
 */
const three_rivers: IVMPreset = {
  id: 'three_rivers',
  name: 'Three Rivers',
  category: 'Patterns',
  extraChannels: ['rippleH', 'rippleV'],
  init() { /* all channels start at 0 */ },
  update(ctx) {
    const { energy } = ctx.current;
    const nextE = ctx.next.energy;
    const rH = ctx.extra.get('rippleH')!;
    const rV = ctx.extra.get('rippleV')!;

    const spawnInterval = 55;
    const travelFrames = 240;
    const dropR2 = 0.006;        // drop head radius²
    const trailDecay = 0.985;
    const cx = 0.5, cy = 0.5;    // convergence point

    // 1. Decay trail
    for (let i = 0; i < ctx.count; i++) nextE[i] = energy[i] * trailDecay;

    // 2. Compute active drops (stateless — derived from frame count)
    const newest = Math.floor(ctx.frame / spawnInterval);
    const oldest = Math.max(0, newest - Math.ceil(travelFrames / spawnInterval) - 1);

    for (let d = oldest; d <= newest; d++) {
      const age = ctx.frame - d * spawnInterval;
      const t = age / travelFrames;
      if (t > 1.05) continue;

      // Deterministic spread: each drop starts at a slightly different offset
      const hash = ((d * 7919 + 13) % 100) / 100;       // 0..1
      const perpSpread = (hash - 0.5) * 0.25;

      // Origin: top-right edge, spread perpendicular to travel direction
      const sx = 0.92 + perpSpread * -0.35;
      const sy = 0.08 + perpSpread * 0.35;

      // Current position: ease-in toward center
      const ease = t * t * (3 - 2 * t);                  // smoothstep
      const px = sx + (cx - sx) * Math.min(ease, 1);
      const py = sy + (cy - sy) * Math.min(ease, 1);

      // Paint drop head
      for (let i = 0; i < ctx.count; i++) {
        const dx = ctx.normalizedX[i] - px;
        const dy = ctx.normalizedY[i] - py;
        const d2 = dx * dx + dy * dy;
        if (d2 < dropR2) {
          nextE[i] = Math.max(nextE[i], 1 - d2 / dropR2);
        }
      }

      // On impact → trigger ripple
      if (age === travelFrames) {
        for (let i = 0; i < ctx.count; i++) {
          const dx = ctx.normalizedX[i] - cx;
          const dy = ctx.normalizedY[i] - cy;
          if (dx * dx + dy * dy < 0.004) {
            rH[i] = 0.9;
            rV[i] = 0;
          }
        }
      }
    }

    // 3. Ripple wave equation (on triangle grid)
    const c2 = 0.2, vDamp = 0.98, hDamp = 0.994;
    for (let i = 0; i < ctx.count; i++) {
      const avg = avgNeighbor(rH, ctx, i);
      rV[i] += (avg - rH[i]) * c2;
      rV[i] *= vDamp;
      rH[i] = (rH[i] + rV[i]) * hDamp;
      if (rH[i] > 1) rH[i] = 1;
      else if (rH[i] < -1) rH[i] = -1;
    }

    // 4. Combine trail + ripple
    for (let i = 0; i < ctx.count; i++) {
      nextE[i] = clamp01(Math.max(nextE[i], Math.abs(rH[i])));
    }

    // Mouse creates ripples
    if (ctx.mouseActive) {
      for (let i = 0; i < ctx.count; i++) {
        const dx = ctx.normalizedX[i] - ctx.mouseX;
        const dy = ctx.normalizedY[i] - ctx.mouseY;
        if (dx * dx + dy * dy < 0.003) {
          rH[i] = 0.8;
          rV[i] = 0;
        }
      }
    }
  },
  visual(i, ctx) {
    const e = ctx.current.energy[i];
    const ripple = Math.abs(ctx.extra.get('rippleH')![i]);
    // Ripple dominates in gold; trail shows in cyan
    const isRipple = ripple > 0.06 && ripple >= e * 0.5;
    return {
      opacity: 0.04 + clamp01(Math.max(e, ripple)) * 0.9,
      scale: 1,
      rotation: 0,
      color: isRipple
        ? lutLookup(RIVER_LUTS[2], clamp01(ripple))
        : lutLookup(RIVER_LUTS[0], clamp01(e)),
    };
  },
};

/** 15. fibonacci_rain — Golden-angle spiral galaxy with rain-like energy pulses */
const fibonacci_rain: IVMPreset = {
  id: 'fibonacci_rain',
  name: 'Fibonacci Rain',
  category: 'Patterns',
  extraChannels: ['dist', 'angle'],
  init(ctx) {
    const distBuf = ctx.extra.get('dist')!;
    const angleBuf = ctx.extra.get('angle')!;
    for (let i = 0; i < ctx.count; i++) {
      const dx = ctx.normalizedX[i] - 0.5;
      const dy = ctx.normalizedY[i] - 0.5;
      distBuf[i] = Math.sqrt(dx * dx + dy * dy);
      angleBuf[i] = Math.atan2(dy, dx);
      ctx.current.energy[i] = 0;
    }
  },
  update(ctx) {
    const { energy } = ctx.current;
    const nextE = ctx.next.energy;
    const distBuf = ctx.extra.get('dist')!;
    const angleBuf = ctx.extra.get('angle')!;

    // Golden angle ≈ 137.508° — the Fibonacci spiral signature
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const nArms = 3;         // trinity spiral arms
    const twist = 4.0;       // logarithmic tightness
    const rotSpeed = 0.15;   // slow galaxy rotation
    const t = ctx.elapsed * rotSpeed;

    for (let i = 0; i < ctx.count; i++) {
      const r = distBuf[i];
      const theta = angleBuf[i];

      // Logarithmic spiral: θ - ln(r) * twist wraps around arms
      const spiralPhase = nArms * (theta - Math.log(r + 0.01) * twist - t);
      // Arm proximity: 1 on arm center, 0 between arms
      const armProximity = (Math.cos(spiralPhase) + 1) / 2;

      // Radial glow: bright center fading outward
      const radialGlow = Math.max(0, 1 - r * 1.8);

      // Rain pulses: periodic energy drops along the arms
      const rainPhase = r * 20 - ctx.elapsed * 2.5;
      const rain = Math.max(0, Math.sin(rainPhase)) * armProximity;

      // Combine: arm structure × radial falloff + rain sparkle
      const target = clamp01(armProximity * radialGlow * 0.8 + rain * 0.5);

      // Smooth chase toward target with decay
      nextE[i] = energy[i] + (target - energy[i]) * 0.08;
      nextE[i] = clamp01(nextE[i]);
    }
  },
  visual(i, ctx) {
    const e = ctx.current.energy[i];
    const distBuf = ctx.extra.get('dist')!;
    const r = distBuf[i];
    // Gentle rotation: inner triangles rotate faster (differential rotation like a real galaxy)
    const spin = (1 / (r + 0.3)) * 4;
    return {
      opacity: 0.03 + e * 0.95,
      scale: 0.8 + e * 0.2,
      rotation: spin * ctx.elapsed * 8,
      color: lutLookup(LUT.fibonacci, e),
    };
  },
};

// ============================================================
// NETWORK (4) — connectivity, signal propagation, topology
// ============================================================

/** 17. mycelium_network — §01 Network: spores form persistent green filaments
 *
 *  Iterated for the network.trinity.agency hero background. Two-channel
 *  state: `energy` is the active spore (fast-decay, propagating life) and
 *  `filament` is long-term memory (very slow decay) that records every
 *  cell the network ever touched. Together they read as a living mycelium
 *  graph — bright spore tips advancing into virgin cells while a softer
 *  filament structure persists behind them. Periodic re-firing along
 *  high-filament cells keeps the network breathing instead of fading flat.
 *  Mouse drops a fresh spore. Painted in the §01 Network green palette.
 */
const mycelium_network: IVMPreset = {
  id: 'mycelium_network',
  name: 'Mycelium Network',
  category: 'Network',
  extraChannels: ['cooldown', 'filament'],
  init(ctx) {
    const cooldown = ctx.extra.get('cooldown')!;
    const filament = ctx.extra.get('filament')!;
    cooldown.fill(0);
    filament.fill(0);
    const sporeCount = Math.max(3, Math.floor(ctx.count * 0.0025));
    for (let s = 0; s < sporeCount; s++) {
      const idx = Math.floor(Math.random() * ctx.count);
      ctx.current.energy[idx] = 1;
      filament[idx] = 1;
    }
  },
  update(ctx) {
    const { energy } = ctx.current;
    const next = ctx.next.energy;
    const cooldown = ctx.extra.get('cooldown')!;
    const filament = ctx.extra.get('filament')!;

    for (let i = 0; i < ctx.count; i++) {
      // Filament memory: integrates from active energy (slow ramp), then
      // decays an order of magnitude slower than the spore itself, so the
      // network skeleton persists long after the bright spore has moved.
      if (energy[i] > 0.4) {
        filament[i] = Math.min(1, filament[i] + 0.025);
      } else {
        filament[i] *= 0.9988;
      }

      if (cooldown[i] > 0) {
        cooldown[i] -= 1;
        next[i] *= 0.998;
        continue;
      }

      if (energy[i] > 0.5) {
        const [n0, n1, n2] = nb(ctx, i);
        // Slight preference for extending into virgin cells over re-walking
        // existing filament — keeps the network spreading, not just pulsing.
        const virgin: number[] = [];
        const known: number[] = [];
        for (const ni of [n0, n1, n2]) {
          if (ni < 0 || energy[ni] >= 0.5 || cooldown[ni] > 0) continue;
          if (filament[ni] < 0.2) virgin.push(ni);
          else known.push(ni);
        }
        const pool = virgin.length > 0 ? virgin : known;
        if (pool.length > 0 && Math.random() < 0.18) {
          next[pool[Math.floor(Math.random() * pool.length)]] = 1;
        }
        // Occasional spore exhaustion → cooldown, prevents infinite chains
        if (Math.random() < 0.0025) {
          next[i] = 0.25;
          cooldown[i] = 500;
        }
      }

      next[i] *= 0.9994;
    }

    // Periodic re-fire along an established filament — prefer high-filament
    // cells so the network keeps lighting along its own skeleton instead of
    // sputtering out in unrelated noise.
    if (ctx.frame % 220 === 0) {
      let bestIdx = -1;
      let bestF = 0.55;
      for (let trial = 0; trial < 30; trial++) {
        const cand = Math.floor(Math.random() * ctx.count);
        if (filament[cand] > bestF && cooldown[cand] <= 0 && energy[cand] < 0.3) {
          bestF = filament[cand];
          bestIdx = cand;
        }
      }
      if (bestIdx >= 0) next[bestIdx] = 1;
    }

    // Occasional fresh spore in a virgin region — keeps the network from
    // collapsing onto a single attractor over very long runs.
    if (ctx.frame % 900 === 0) {
      let bestIdx = -1;
      let bestF = 1.1;
      for (let trial = 0; trial < 30; trial++) {
        const cand = Math.floor(Math.random() * ctx.count);
        if (filament[cand] < bestF && cooldown[cand] <= 0) {
          bestF = filament[cand];
          bestIdx = cand;
        }
      }
      if (bestIdx >= 0) {
        next[bestIdx] = 1;
        filament[bestIdx] = 1;
      }
    }

    // Mouse drops a fresh spore at the cursor cell
    if (ctx.mouseActive) {
      let bestIdx = -1;
      let bestD = 0.005;
      for (let i = 0; i < ctx.count; i++) {
        const dx = ctx.normalizedX[i] - ctx.mouseX;
        const dy = ctx.normalizedY[i] - ctx.mouseY;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestD) { bestD = d2; bestIdx = i; }
      }
      if (bestIdx >= 0 && cooldown[bestIdx] <= 0) {
        next[bestIdx] = 1;
        filament[bestIdx] = 1;
      }
    }
  },
  visual(i, ctx) {
    const e = ctx.current.energy[i];
    const f = ctx.extra.get('filament')![i];
    // Active spore dominates; filament provides a softer persistent floor.
    const brightness = Math.max(e, f * 0.55);
    return {
      opacity: 0.04 + brightness * 0.92,
      scale: 0.86 + e * 0.20,
      rotation: 0,
      color: lutLookup(LUT.network, brightness),
    };
  },
};

/** 18. synapse_cascade — Pulses fire and branch along the adjacency graph
 *
 *  Random "neurons" fire at a steady cadence. Each pulse propagates to 0–2
 *  of a triangle's 3 neighbors per tick (branching factor weighted toward 1).
 *  Cells flash on arrival, leave a fading trail, then enter a brief
 *  refractory cooldown so the same cell can't immediately re-fire — chains
 *  fan out instead of collapsing back on themselves. Mouse fires the nearest
 *  cell. Reads as a nervous system thinking out loud.
 */
const synapse_cascade: IVMPreset = {
  id: 'synapse_cascade',
  name: 'Synapse Cascade',
  category: 'Network',
  extraChannels: ['head', 'refractory'],
  init(ctx) {
    const head = ctx.extra.get('head')!;
    const seedCount = Math.max(2, Math.floor(ctx.count * 0.0015));
    for (let s = 0; s < seedCount; s++) {
      const i = Math.floor(Math.random() * ctx.count);
      head[i] = 1;
      ctx.current.energy[i] = 1;
    }
  },
  update(ctx) {
    const { energy } = ctx.current;
    const next = ctx.next.energy;
    const head = ctx.extra.get('head')!;
    const refractory = ctx.extra.get('refractory')!;

    // Trail decay + refractory tick-down. `head` is single-buffered extra
    // state, so we mutate it in place during the snapshot+propagate pass.
    for (let i = 0; i < ctx.count; i++) {
      next[i] = energy[i] * 0.90;
      if (refractory[i] > 0) refractory[i] -= 1;
    }

    // Snapshot current heads — they propagate this tick, then clear and
    // enter cooldown. Without the snapshot, a head set by a sibling head
    // earlier in the loop would re-propagate in the same frame.
    const currentHeads: number[] = [];
    for (let i = 0; i < ctx.count; i++) {
      if (head[i] > 0.5) {
        currentHeads.push(i);
        head[i] = 0;
        refractory[i] = 14;
      }
    }

    // Each head branches to 0..2 eligible neighbors
    for (const cur of currentHeads) {
      const [n0, n1, n2] = nb(ctx, cur);
      const candidates: number[] = [];
      for (const ni of [n0, n1, n2]) {
        if (ni >= 0 && head[ni] < 0.5 && refractory[ni] <= 0) candidates.push(ni);
      }
      // Branching: 15% die-off, 60% one child, 25% two children.
      // Keeps cascades alive without exploding to grid-saturating wavefronts.
      const r = Math.random();
      const wantChildren = r < 0.15 ? 0 : r < 0.75 ? 1 : 2;
      for (let k = candidates.length - 1; k > 0; k--) {
        const j = Math.floor(Math.random() * (k + 1));
        [candidates[k], candidates[j]] = [candidates[j], candidates[k]];
      }
      const numChildren = Math.min(wantChildren, candidates.length);
      for (let k = 0; k < numChildren; k++) {
        const ni = candidates[k];
        head[ni] = 1;
        next[ni] = 1;
      }
    }

    // Spontaneous neuron firing keeps activity alive in quiet stretches
    if (ctx.frame % 28 === 0) {
      const i = Math.floor(Math.random() * ctx.count);
      if (head[i] < 0.5 && refractory[i] <= 0) {
        head[i] = 1;
        next[i] = 1;
      }
    }

    // Mouse fires the nearest neuron
    if (ctx.mouseActive) {
      let bestIdx = -1;
      let bestD = 0.005;
      for (let i = 0; i < ctx.count; i++) {
        const dx = ctx.normalizedX[i] - ctx.mouseX;
        const dy = ctx.normalizedY[i] - ctx.mouseY;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestD) { bestD = d2; bestIdx = i; }
      }
      if (bestIdx >= 0 && head[bestIdx] < 0.5 && refractory[bestIdx] <= 0) {
        head[bestIdx] = 1;
        next[bestIdx] = 1;
      }
    }
  },
  visual(i, ctx) {
    const e = ctx.current.energy[i];
    const isHead = ctx.extra.get('head')![i] > 0.5;
    return {
      opacity: 0.06 + e * 0.94,
      scale: 0.86 + e * (isHead ? 0.30 : 0.18),
      rotation: isHead ? Math.sin(ctx.elapsed * 8 + i * 0.13) * 2 : 0,
      color: isHead ? '#ffffff' : lutLookup(LUT.storm, e),
    };
  },
};

const CONST_NUM_NODES = 20;
const CONST_NUM_LINKS = 8;
const CONST_LINK_LIFE = 180;
const CONST_INACTIVE_BIRTH = -9999;

/** 19. constellation — Sparse nodes, paths through the mosaic
 *
 *  ~20 well-spread cells become "nodes" (Mitchell's best-candidate placement)
 *  and pulse at a slow shared cadence. ~8 active "links" continuously rewire
 *  between random node pairs. Each link is rasterised onto the triangle grid
 *  by greedy adjacency walk: from the source cell, step to whichever of the
 *  three neighbours sits closest to the target centroid, until you arrive.
 *  Cells along the path light up; links fade in/hold/fade out and respawn.
 *  Reads as a knowledge graph or social network breathing.
 */
const constellation: IVMPreset = {
  id: 'constellation',
  name: 'Constellation',
  category: 'Network',
  extraChannels: ['nodeIdx', 'linkBright', 'linkA', 'linkB', 'linkBirth'],
  init(ctx) {
    const nodeIdx = ctx.extra.get('nodeIdx')!;
    const linkA = ctx.extra.get('linkA')!;
    const linkB = ctx.extra.get('linkB')!;
    const linkBirth = ctx.extra.get('linkBirth')!;

    // Mitchell's best-candidate: pick first node randomly, then for each
    // subsequent node sample 25 candidates and keep the one with maximum
    // minimum distance to existing picks. Yields well-spread node positions.
    const targetN = Math.min(CONST_NUM_NODES, Math.max(3, Math.floor(ctx.count / 25)));
    const picks: number[] = [Math.floor(Math.random() * ctx.count)];
    while (picks.length < targetN) {
      let bestI = -1;
      let bestMinD = -1;
      for (let trial = 0; trial < 25; trial++) {
        const cand = Math.floor(Math.random() * ctx.count);
        let minD = Infinity;
        for (const p of picks) {
          const dx = ctx.normalizedX[cand] - ctx.normalizedX[p];
          const dy = ctx.normalizedY[cand] - ctx.normalizedY[p];
          const d = dx * dx + dy * dy;
          if (d < minD) minD = d;
        }
        if (minD > bestMinD) { bestMinD = minD; bestI = cand; }
      }
      picks.push(bestI);
    }

    for (let k = 0; k < CONST_NUM_NODES; k++) {
      nodeIdx[k] = k < picks.length ? picks[k] : -1;
    }

    // Stagger link births so they don't all appear simultaneously
    const targetLinks = Math.min(CONST_NUM_LINKS, Math.max(2, Math.floor(picks.length * 0.4)));
    for (let k = 0; k < CONST_NUM_LINKS; k++) {
      if (k >= targetLinks || picks.length < 2) {
        linkA[k] = -1;
        linkB[k] = -1;
        linkBirth[k] = CONST_INACTIVE_BIRTH;
        continue;
      }
      const a = picks[Math.floor(Math.random() * picks.length)];
      let b = picks[Math.floor(Math.random() * picks.length)];
      while (b === a) b = picks[Math.floor(Math.random() * picks.length)];
      linkA[k] = a;
      linkB[k] = b;
      // Negative birth = pre-roll; lifetime starts staggered across [-LIFE, 0]
      linkBirth[k] = -Math.floor(Math.random() * CONST_LINK_LIFE);
    }
  },
  update(ctx) {
    const linkBright = ctx.extra.get('linkBright')!;
    const nodeIdx = ctx.extra.get('nodeIdx')!;
    const linkA = ctx.extra.get('linkA')!;
    const linkB = ctx.extra.get('linkB')!;
    const linkBirth = ctx.extra.get('linkBirth')!;
    const nextE = ctx.next.energy;

    // Decay residual link brightness (gives links a soft trailing afterglow)
    for (let i = 0; i < ctx.count; i++) linkBright[i] *= 0.88;

    // Count valid nodes (init may have placed fewer than CONST_NUM_NODES)
    let nodeCount = 0;
    for (let k = 0; k < CONST_NUM_NODES; k++) {
      if (nodeIdx[k] >= 0) nodeCount++;
      else break;
    }

    for (let k = 0; k < CONST_NUM_LINKS; k++) {
      if (linkBirth[k] === CONST_INACTIVE_BIRTH) continue;
      const age = ctx.frame - linkBirth[k];

      // Respawn expired link with a fresh random pair of distinct nodes
      if (age >= CONST_LINK_LIFE && nodeCount >= 2) {
        const ai = Math.floor(Math.random() * nodeCount);
        let bi = Math.floor(Math.random() * nodeCount);
        while (bi === ai) bi = Math.floor(Math.random() * nodeCount);
        linkA[k] = nodeIdx[ai];
        linkB[k] = nodeIdx[bi];
        linkBirth[k] = ctx.frame;
        continue;
      }
      if (age < 0) continue;

      // Brightness envelope: ease in 0..0.18, hold, ease out 0.82..1
      const t = age / CONST_LINK_LIFE;
      let env: number;
      if (t < 0.18) env = t / 0.18;
      else if (t > 0.82) env = (1 - t) / 0.18;
      else env = 1;
      env *= 0.88;

      // Greedy adjacency walk from A toward B's centroid
      const startIdx = linkA[k];
      const endIdx = linkB[k];
      if (startIdx < 0 || endIdx < 0) continue;
      if (startIdx >= ctx.count || endIdx >= ctx.count) continue;
      const ex = ctx.normalizedX[endIdx];
      const ey = ctx.normalizedY[endIdx];
      let cur = startIdx;
      const visited = new Set<number>();
      for (let steps = 0; steps < 250; steps++) {
        if (visited.has(cur)) break;
        visited.add(cur);
        if (linkBright[cur] < env) linkBright[cur] = env;
        if (cur === endIdx) break;
        const [n0, n1, n2] = nb(ctx, cur);
        let best = -1;
        let bestD = Infinity;
        for (const ni of [n0, n1, n2]) {
          if (ni < 0 || visited.has(ni)) continue;
          const dx = ctx.normalizedX[ni] - ex;
          const dy = ctx.normalizedY[ni] - ey;
          const d = dx * dx + dy * dy;
          if (d < bestD) { bestD = d; best = ni; }
        }
        if (best < 0) break;
        cur = best;
      }
    }

    for (let i = 0; i < ctx.count; i++) {
      nextE[i] = clamp01(linkBright[i]);
    }

    // Nodes pulse at a shared cadence — visible even between active links
    const nodePulse = 0.55 + 0.22 * Math.sin(ctx.elapsed * 1.4);
    for (let k = 0; k < nodeCount; k++) {
      const ni = nodeIdx[k];
      if (ni >= 0 && ni < ctx.count) {
        nextE[ni] = Math.max(nextE[ni], nodePulse);
      }
    }
  },
  visual(i, ctx) {
    const e = ctx.current.energy[i];
    return {
      opacity: 0.04 + e * 0.96,
      scale: 0.85 + e * 0.22,
      rotation: 0,
      color: lutLookup(LUT.mycelium, e),
    };
  },
};

const MESH_NUM_SOURCES = 3;
const MESH_CYCLE_LENGTH = 720;     // frames per cycle (~12s at 60fps)
const MESH_WAVE_SPEED = 0.085;     // BFS hops per frame
const MESH_WAVE_SIGMA = 1.8;       // wavefront band width in hops

/** 20. mesh_wavefronts — Concurrent BFS waves from boundary sources interfere
 *
 *  Three "broadcast" sources at well-separated boundary positions fire
 *  simultaneous BFS wavefronts inward through the triangle adjacency graph.
 *  Each cell's brightness is the sum of three Gaussian bands centred on
 *  each source's growing radius — where wavefronts overlap, the cell
 *  brightens additively, tracing a clean interference locus across the
 *  mosaic. Sources reset to fresh corners every cycle. The network here
 *  isn't drawn; it's implicit in the timing. Reads as gossip protocol /
 *  packet-flood discovery.
 *
 *  BFS is precomputed once per cycle (every ~12s); per-frame cost is just
 *  three exp() per cell.
 */
const mesh_wavefronts: IVMPreset = {
  id: 'mesh_wavefronts',
  name: 'Mesh Wavefronts',
  category: 'Network',
  extraChannels: ['srcIdx', 'distA', 'distB', 'distC', 'cycleStart'],
  init(ctx) {
    pickMeshSources(ctx);
    computeAllMeshBFS(ctx);
    ctx.extra.get('cycleStart')![0] = 0;
  },
  update(ctx) {
    const cycleStart = ctx.extra.get('cycleStart')!;
    const distA = ctx.extra.get('distA')!;
    const distB = ctx.extra.get('distB')!;
    const distC = ctx.extra.get('distC')!;
    const nextE = ctx.next.energy;

    let elapsed = ctx.frame - cycleStart[0];
    if (elapsed >= MESH_CYCLE_LENGTH) {
      pickMeshSources(ctx);
      computeAllMeshBFS(ctx);
      cycleStart[0] = ctx.frame;
      elapsed = 0;
    }

    const radius = elapsed * MESH_WAVE_SPEED;
    const tFrac = elapsed / MESH_CYCLE_LENGTH;
    // Cycle envelope: gentle ramp at boundaries to avoid hard cuts on reset
    const env = tFrac < 0.08 ? tFrac / 0.08
      : tFrac > 0.92 ? (1 - tFrac) / 0.08
      : 1;
    const inv2sigma2 = 1 / (2 * MESH_WAVE_SIGMA * MESH_WAVE_SIGMA);

    for (let i = 0; i < ctx.count; i++) {
      let v = 0;
      const dA = distA[i];
      if (dA >= 0) {
        const d = dA - radius;
        v += Math.exp(-d * d * inv2sigma2);
      }
      const dB = distB[i];
      if (dB >= 0) {
        const d = dB - radius;
        v += Math.exp(-d * d * inv2sigma2);
      }
      const dC = distC[i];
      if (dC >= 0) {
        const d = dC - radius;
        v += Math.exp(-d * d * inv2sigma2);
      }
      nextE[i] = clamp01(v * env);
    }
  },
  visual(i, ctx) {
    const e = ctx.current.energy[i];
    return {
      opacity: 0.04 + e * 0.96,
      scale: 0.88 + e * 0.20,
      rotation: 0,
      color: lutLookup(LUT.cathedral, e),
    };
  },
};

/** Pick MESH_NUM_SOURCES well-separated boundary cells as wavefront sources. */
function pickMeshSources(ctx: IVMContext): void {
  const srcIdx = ctx.extra.get('srcIdx')!;
  // Eight candidate boundary anchors; shuffle and keep first N
  const targets: [number, number][] = [
    [0.06, 0.5], [0.94, 0.5],
    [0.5, 0.06], [0.5, 0.94],
    [0.10, 0.10], [0.90, 0.90],
    [0.10, 0.90], [0.90, 0.10],
  ];
  for (let i = targets.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [targets[i], targets[j]] = [targets[j], targets[i]];
  }
  for (let s = 0; s < MESH_NUM_SOURCES; s++) {
    const [tx, ty] = targets[s];
    let bestIdx = 0;
    let bestD = Infinity;
    for (let i = 0; i < ctx.count; i++) {
      const dx = ctx.normalizedX[i] - tx;
      const dy = ctx.normalizedY[i] - ty;
      const d = dx * dx + dy * dy;
      if (d < bestD) { bestD = d; bestIdx = i; }
    }
    srcIdx[s] = bestIdx;
  }
}

function computeAllMeshBFS(ctx: IVMContext): void {
  const srcIdx = ctx.extra.get('srcIdx')!;
  bfsMeshFrom(ctx, srcIdx[0], ctx.extra.get('distA')!);
  bfsMeshFrom(ctx, srcIdx[1], ctx.extra.get('distB')!);
  bfsMeshFrom(ctx, srcIdx[2], ctx.extra.get('distC')!);
}

/** Standard BFS on the triangle adjacency graph. Writes hop count into
 *  `dist`, with -1 for unreachable cells (e.g. across a silhouette gap). */
function bfsMeshFrom(ctx: IVMContext, src: number, dist: Float32Array): void {
  for (let i = 0; i < ctx.count; i++) dist[i] = -1;
  dist[src] = 0;
  const queue: number[] = [src];
  let head = 0;
  while (head < queue.length) {
    const cur = queue[head++];
    const d = dist[cur];
    const [n0, n1, n2] = nb(ctx, cur);
    for (const ni of [n0, n1, n2]) {
      if (ni >= 0 && dist[ni] < 0) {
        dist[ni] = d + 1;
        queue.push(ni);
      }
    }
  }
}

// ============================================================
// FILTERS (2)
// ============================================================

/** 19. ghost_triangles — Shows only change (EMA diff) */
const ghost_triangles: IVMFilter = {
  id: 'ghost_triangles',
  name: 'Ghost Triangles',
  extraChannels: ['ema'],
  init(ctx) {
    const ema = ctx.extra.get('ema')!;
    for (let i = 0; i < ctx.count; i++) ema[i] = ctx.current.energy[i];
  },
  postUpdate(ctx) {
    const ema = ctx.extra.get('ema')!;
    for (let i = 0; i < ctx.count; i++) {
      ema[i] = ema[i] * 0.99 + ctx.current.energy[i] * 0.01;
    }
  },
  transform(i, base, ctx) {
    const diff = Math.abs(ctx.current.energy[i] - ctx.extra.get('ema')![i]) * 3;
    return { ...base, opacity: clamp01(diff) };
  },
};

/** 20. ancestor_echo — Snapshot blending from past frames */
const ancestor_echo: IVMFilter = {
  id: 'ancestor_echo',
  name: 'Ancestor Echo',
  extraChannels: ['snap0', 'snap1', 'snap2', 'snap3', 'snap4', 'snap5'],
  init(ctx) {
    for (let s = 0; s < 6; s++) ctx.extra.get(`snap${s}`)!.set(ctx.current.energy);
  },
  postUpdate(ctx) {
    if (ctx.frame % 60 === 0) {
      for (let s = 5; s > 0; s--) ctx.extra.get(`snap${s}`)!.set(ctx.extra.get(`snap${s - 1}`)!);
      ctx.extra.get('snap0')!.set(ctx.current.energy);
    }
  },
  transform(i, base, ctx) {
    const blended = ctx.current.energy[i] * 0.6
      + ctx.extra.get('snap3')![i] * 0.3
      + ctx.extra.get('snap5')![i] * 0.1;
    return { ...base, opacity: clamp01(blended) };
  },
};

// ============================================================
// REGISTRIES
// ============================================================

export const IVM_PRESETS: IVMPreset[] = [
  cathedral_whisper, thermal_gradient,
  conway_trinity, wound_healing,
  breathing_lattice, zero_point_field,
  three_rivers, fibonacci_rain,
  mycelium_network, synapse_cascade, constellation, mesh_wavefronts,
];

export const IVM_FILTERS: IVMFilter[] = [ghost_triangles, ancestor_echo];

export const IVM_PRESET_MAP = new Map<string, IVMPreset>(IVM_PRESETS.map(p => [p.id, p]));
export const IVM_FILTER_MAP = new Map<string, IVMFilter>(IVM_FILTERS.map(f => [f.id, f]));

export const IVM_CATEGORIES = [
  { name: 'Diffusion', presets: IVM_PRESETS.filter(p => p.category === 'Diffusion') },
  { name: 'Life', presets: IVM_PRESETS.filter(p => p.category === 'Life') },
  { name: 'Oscillators', presets: IVM_PRESETS.filter(p => p.category === 'Oscillators') },
  { name: 'Patterns', presets: IVM_PRESETS.filter(p => p.category === 'Patterns') },
  { name: 'Network', presets: IVM_PRESETS.filter(p => p.category === 'Network') },
];
